import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import IcecreamRoundedIcon from "@mui/icons-material/IcecreamRounded";
import { useNavigate } from "react-router-dom";
import Sidebar from "../AdminPanel/Sidebar";
import TopBar from "../AdminPanel/TopBar";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

export default function Complement() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const { showSuccess, showError } = useAlert();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");

  const rowsPerPage = isMobile ? 10 : 30;
  const token = localStorage.getItem("accessToken");

  const [sabores, setSabores] = useState([]);
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedSabor, setSelectedSabor] = useState(null);

  const [form, setForm] = useState({
    nombre: "",
    activo: true,
  });

  const loadSabores = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (search.trim()) {
        params.append("q", search.trim());
      }

      if (estadoFilter) {
        params.append("activo", estadoFilter);
      }

      const response = await fetch(`/api/ventas/sabores/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudieron cargar los sabores");
        return;
      }

      setSabores(data.results || []);
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(0);
      loadSabores();
    }, 350);

    return () => clearTimeout(delay);
  }, [search, estadoFilter]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  const visibleSabores = useMemo(() => {
    const start = page * rowsPerPage;
    return sabores.slice(start, start + rowsPerPage);
  }, [sabores, page, rowsPerPage]);

  const buttonSx = {
    ...dashboardTheme.buttonStyles.contained,
    fontWeight: 700,
  };

  const outlinedButtonSx = {
    minHeight: 38,
    px: 2,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const dangerButtonSx = {
    minHeight: 38,
    px: 2,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    bgcolor: "#dc2626",
    color: "#ffffff",
    boxShadow: "none",
    "&:hover": {
      bgcolor: "#b91c1c",
      boxShadow: "none",
    },
  };

  const openCreate = () => {
    setSelectedSabor(null);
    setForm({
      nombre: "",
      activo: true,
    });
    setFormOpen(true);
  };

  const openEdit = (sabor) => {
    setSelectedSabor(sabor);
    setForm({
      nombre: sabor.nombre || "",
      activo: sabor.activo !== undefined ? sabor.activo : true,
    });
    setFormOpen(true);
  };

  const openDelete = (sabor) => {
    setSelectedSabor(sabor);
    setDeleteOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  const closeDelete = () => {
    if (saving) return;
    setDeleteOpen(false);
  };

  const saveSabor = async () => {
    if (!form.nombre.trim()) {
      showError("El nombre del sabor es obligatorio");
      return;
    }

    try {
      setSaving(true);

      const isEdit = !!selectedSabor?.id;
      const url = isEdit
        ? `/api/ventas/sabores/${selectedSabor.id}/actualizar/`
        : "/api/ventas/sabores/crear/";

      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          activo: form.activo,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const error = data.error;

        if (typeof error === "object") {
          showError(Object.values(error).flat().join(" "));
        } else {
          showError(error || data.detail || "No se pudo guardar el sabor");
        }

        return;
      }

      showSuccess(data.mensaje || "Sabor guardado correctamente");
      setFormOpen(false);
      loadSabores();
    } catch {
      showError("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const deleteSabor = async () => {
    if (!selectedSabor?.id) return;

    try {
      setSaving(true);

      const response = await fetch(`/api/ventas/sabores/${selectedSabor.id}/eliminar/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo eliminar el sabor");
        return;
      }

      showSuccess(data.mensaje || "Sabor eliminado correctamente");
      setDeleteOpen(false);
      loadSabores();
    } catch {
      showError("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: "95%" },
              mx: "auto",
              mt: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => navigate("/admin-panel/ordenes")}
                  sx={{
                    ...outlinedButtonSx,
                    mb: 1.2,
                  }}
                >
                  Volver
                </Button>

                <Typography
                  sx={{
                    fontSize: { xs: 24, sm: 26, md: 30 },
                    fontWeight: 600,
                    color: dashboardTheme.primary_color,
                    lineHeight: 1.2,
                  }}
                >
                  Sabores de helado
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={openCreate}
                sx={buttonSx}
              >
                Nuevo sabor
              </Button>
            </Box>

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box
                sx={{
                  ...dashboardTheme.tableStyles.toolbar,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 1.2,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar sabor"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    maxWidth: { xs: "100%", md: 420 },
                    ...dashboardTheme.inputStyles,
                  }}
                />

                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    maxWidth: { xs: "100%", md: 210 },
                    ...dashboardTheme.inputStyles,
                  }}
                >
                  <InputLabel>Estado</InputLabel>
                  <Select
                    label="Estado"
                    value={estadoFilter}
                    onChange={(e) => setEstadoFilter(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Activo</MenuItem>
                    <MenuItem value="false">Inactivo</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={{ ...dashboardTheme.tableStyles.headCell, minWidth: 260 }}>
                          Sabor
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Estado
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visibleSabores.map((sabor) => (
                        <TableRow key={sabor.id} hover sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: "11px",
                                  display: "grid",
                                  placeItems: "center",
                                  color: dashboardTheme.primary_color,
                                  bgcolor: `${dashboardTheme.primary_color}18`,
                                  flexShrink: 0,
                                }}
                              >
                                <IcecreamRoundedIcon sx={{ fontSize: 20 }} />
                              </Box>

                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: dashboardTheme.text_color,
                                  lineHeight: 1.25,
                                }}
                              >
                                {sabor.nombre || "-"}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {sabor.activo ? "Activo" : "Inactivo"}
                          </TableCell>

                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            <Stack direction="row" spacing={0.8} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditRoundedIcon />}
                                onClick={() => openEdit(sabor)}
                                sx={outlinedButtonSx}
                              >
                                Editar
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineRoundedIcon />}
                                onClick={() => openDelete(sabor)}
                                sx={{
                                  minHeight: 38,
                                  px: 2,
                                  borderRadius: "10px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  boxShadow: "none",
                                }}
                              >
                                Eliminar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}

                      {!loading && visibleSabores.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            No hay sabores registrados
                          </TableCell>
                        </TableRow>
                      )}

                      {loading && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            Cargando sabores...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={sabores.length}
                page={page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={dashboardTheme.tableStyles.pagination}
              />
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: dashboardTheme.primary_color,
            fontWeight: 600,
            fontSize: 18,
          }}
        >
          {selectedSabor ? "Editar sabor" : "Nuevo sabor"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del sabor"
              value={form.nombre}
              onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
              sx={dashboardTheme.inputStyles}
            />

            <FormControl fullWidth size="small" sx={dashboardTheme.inputStyles}>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={form.activo ? "true" : "false"}
                onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.value === "true" }))}
              >
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={closeForm} disabled={saving} sx={outlinedButtonSx}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={saveSabor} disabled={saving} sx={buttonSx}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={closeDelete}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#dc2626",
            fontWeight: 600,
            fontSize: 18,
          }}
        >
          Eliminar sabor
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              color: dashboardTheme.text_color,
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            ¿Deseas eliminar el sabor {selectedSabor?.nombre || "seleccionado"}?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={closeDelete} disabled={saving} sx={outlinedButtonSx}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={deleteSabor} disabled={saving} sx={dangerButtonSx}>
            {saving ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}